import {
  Box,
  Grid,
  Divider,
  Typography,
  Container,
  Stack,
  Link,
} from '@mui/material';
import logo from '../../../../assets/logos/hertex-logo-light.svg';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PinterestIcon from '@mui/icons-material/Pinterest';
import SvgIcon from '@mui/material/SvgIcon';
import {
  fabricsData,
  companyData,
  contactData,
  wallpaperData,
} from './footerData';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useState } from 'react';
import { fetchNavigationTree, NavTreeNode } from '../../../../api/apiNavigation';
import { SHOW_REGISTER_NAV, isRegisterNavLabel } from '../navConfig';

type FooterAddOn = {
  name: string;
  url?: string | null;
  categoryId?: string | null;
  slug?: string | null;
};

// Custom / nav add-on children of a CMS category (the items that also appear
// in the navbar) — so the footer can mirror them.
const extractAddOns = (node?: NavTreeNode): FooterAddOn[] => {
  if (!node?.children) return [];
  return node.children
    .filter(
      (child) =>
        (child.category_type === 'nav_add_on' &&
          child.is_active === true &&
          child.consumer_use_switch === 'on' &&
          (child.consumer_link_url || child.consumer_link_category_id)) ||
        (child.category_type === 'custom' && child.is_published)
    )
    .sort((a, b) => (a.position_order ?? 999) - (b.position_order ?? 999))
    .map((child) => {
      const name = child.display_name || child.category_name;
      if (child.category_type === 'custom') {
        return { name, slug: name.toLowerCase().replace(/\s+/g, '-') };
      }
      return {
        name,
        url: child.consumer_link_url,
        categoryId: child.consumer_link_category_id,
      };
    })
    .filter((addon) => SHOW_REGISTER_NAV || !isRegisterNavLabel(addon.name));
};

function Footer() {
  const dateNow = new Date();
  const [brandsContent, setBrandsContent] = useState<string[]>([]);
  const [companyAddOns, setCompanyAddOns] = useState<FooterAddOn[]>([]);
  const [fabricAddOns, setFabricAddOns] = useState<FooterAddOn[]>([]);

  const navigate = useNavigate();

  // Pull the CMS nav tree: Brands list plus the custom / nav add-ons
  // (Fabric, Company) so the footer mirrors what the navbar shows.
  useQuery('navigationTree', fetchNavigationTree, {
    onSuccess: (data) => {
      const brandsGroup = data.find(
        (item: NavTreeNode) => item.category_name === 'Brands'
      );
      const brands = (brandsGroup?.children ?? [])
        // Only actual brand nodes — never let other category types
        // (item_group, group, etc. e.g. Rugs, Furniture) slip in
        .filter((child) => child.category_type === 'brand' && child.is_published)
        .map((child) => child.display_name || child.category_name);

      // Internationals is a curated page, always pinned (deduped)
      if (!brands.some((name) => name.toLowerCase() === 'internationals')) {
        brands.push('Internationals');
      }
      setBrandsContent(brands);

      setCompanyAddOns(
        extractAddOns(data.find((item) => item.category_name === 'Company'))
      );
      setFabricAddOns(
        extractAddOns(data.find((item) => item.category_name === 'Fabric'))
      );
    },
    onError: (error) => {
      console.error('Error fetching navigation tree:', error);
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Internal links navigate in the same tab; external links open a new tab.
  const handleAddOnClick = (addon: FooterAddOn) => {
    if (addon.slug) {
      navigate(`/${addon.slug}`);
      return;
    }
    if (addon.url) {
      const internalHosts = [
        'hertex-staging.net',
        'hertex.co.za',
        'www.hertex.co.za',
      ];
      try {
        const parsed = new URL(addon.url, window.location.origin);
        if (
          parsed.origin === window.location.origin ||
          internalHosts.includes(parsed.hostname)
        ) {
          navigate(parsed.pathname + parsed.search);
          return;
        }
        window.open(addon.url, '_blank');
      } catch {
        navigate(addon.url);
      }
      return;
    }
    if (addon.categoryId) {
      navigate(`/category/${addon.categoryId}`);
    }
  };

  return (
    <Grid
      sx={{
        backgroundColor: '#000000',
        width: '100%',
        color: '#fff',
        padding: { xs: '20px 0', md: '80px 64px' },
      }}
    >
      <Container maxWidth="xl">
        {/* Desktop */}

        <Box
          onClick={() => navigate('/')}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '93px',
            cursor: 'pointer',
          }}
        >
          <img
            style={{ width: '221px', height: '43px' }}
            src={logo}
            alt="logo"
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            justifyContent: 'space-between',

            paddingBottom: '64px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: '20px',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                mb: 2,
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                lineHeight: '18px',
                cursor: 'pointer',
                color: '#FFF',
              }}
              onClick={() => navigate('../item_group/005')}
            >
              Fabric
            </Typography>
            {fabricsData.map((fabrics) => (
              <Typography
                variant="caption"
                sx={{
                  color: '#9A9A9A',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  paddingBottom: '5px',
                  cursor: 'pointer',
                }}
                onClick={() => navigate(fabrics.link)}
              >
                {fabrics.subtitle}
              </Typography>
            ))}
            {fabricAddOns
              .filter(
                (addon) =>
                  !fabricsData.some(
                    (f) =>
                      f.subtitle.toLowerCase() === addon.name.toLowerCase()
                  )
              )
              .map((addon, index) => (
              <Typography
                key={`fabric-addon-${index}`}
                variant="caption"
                sx={{
                  color: '#9A9A9A',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  paddingBottom: '5px',
                  cursor: 'pointer',
                }}
                onClick={() => handleAddOnClick(addon)}
              >
                {addon.name}
              </Typography>
            ))}
            <Typography
              variant="caption"
              sx={{
                mb: 2,
                mt: 2,
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                lineHeight: '18px',
                cursor: 'pointer',
                color: '#FFF',
              }}
              onClick={() =>
                navigate('../item_group/001/application/hertex wallpaper')
              }
            >
              Wallpaper
            </Typography>
            {wallpaperData.map((wallpaper) => (
              <Typography
                variant="caption"
                sx={{
                  color: '#9A9A9A',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  paddingBottom: '5px',
                  cursor: 'pointer',
                }}
                onClick={() => navigate(wallpaper.link)}
              >
                {wallpaper.subtitle}
              </Typography>
            ))}
          </Box>

          <Divider
            sx={{
              backgroundColor: '#6E6E6E',
              width: '1px',
            }}
          ></Divider>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  // mb: 2,
                  fontSize: '14px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  lineHeight: '18px',
                  pb: '10px',
                  color: '#FFF',
                }}
              >
                Rugs
              </Typography>
              <a
                href="https://hertexhaus.co.za/pages/rugs"
                target="_blank"
                style={{
                  textDecoration: 'none',
                }}
                rel="noreferrer"
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#9A9A9A',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  shop rugs online @ hertexhaus.co.za
                </Typography>
              </a>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mb: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#FFF',
                  fontSize: '14px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  lineHeight: '18px',
                  pb: '10px',
                }}
              >
                Homeware
              </Typography>
              <a
                href="https://hertexhaus.co.za/"
                target="_blank"
                style={{
                  textDecoration: 'none',
                }}
                rel="noreferrer"
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#9A9A9A',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    pb: '10px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  shop homeware online @ hertexhaus.co.za
                </Typography>
              </a>
            </Box>
          </Box>

          <Divider
            sx={{
              backgroundColor: '#6E6E6E',
              width: '1px',
            }}
          ></Divider>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: '20px',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#FFF',
                mb: 2,
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                lineHeight: '18px',
              }}
            >
              Brands
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: '0', lg: '35px' },
                justifyContent: 'space-between',
              }}
            >
              <Stack sx={{ flexWrap: 'wrap', maxWidth: 100 }} direction="row">
                {brandsContent?.sort().map((brand, index) => (
                  <Typography
                    variant="caption"
                    key={index}
                    sx={{
                      color: '#9A9A9A',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      width: '100%',
                      marginBottom: 1,
                      ':nth-of-type(n+7)': {
                        width: '100%',
                        marginTop: 0,
                        clear: 'left',
                      },
                    }}
                    onClick={() => {
                      if (brand === 'HAUS') {
                        window.open('https://hertexhaus.co.za/', '_blank');
                      } else if (brand === 'Internationals') {
                        navigate('/internationals');
                      } else {
                        navigate(`/item_group/9000/brand/${brand}`);
                      }
                    }}
                  >
                    {brand}
                  </Typography>
                ))}
              </Stack>
            </Box>
          </Box>
          <Divider
            sx={{
              backgroundColor: '#6E6E6E',
              width: '1px',
            }}
          ></Divider>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#FFF',
                mb: 2,
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                lineHeight: '18px',
              }}
            >
              Company
            </Typography>
            {companyData
              .filter((companies) => SHOW_REGISTER_NAV || !isRegisterNavLabel(companies.subtitle))
              .map((companies) => (
              <Typography
                variant="caption"
                sx={{
                  color: '#9A9A9A',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  paddingBottom: '5px',
                  cursor: 'pointer',
                }}
                onClick={() => navigate(companies.link)}
              >
                {companies.subtitle}
              </Typography>
            ))}
            {companyAddOns.map((addon, index) => (
              <Typography
                key={`company-addon-${index}`}
                variant="caption"
                sx={{
                  color: '#9A9A9A',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  paddingBottom: '5px',
                  cursor: 'pointer',
                }}
                onClick={() => handleAddOnClick(addon)}
              >
                {addon.name}
              </Typography>
            ))}
          </Box>
          <Divider
            sx={{
              backgroundColor: '#6E6E6E',
              width: '1px',
            }}
          ></Divider>
          <Box
            sx={{
              pt: { xs: '40px', lg: '0' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#FFF',
                mb: 2,
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                lineHeight: '18px',
              }}
            >
              Contact
            </Typography>
            {contactData.map((contact) => (
              <Box sx={{ pb: '20px', display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="caption"
                  sx={{ color: '#9A9A9A', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', paddingBottom: '5px' }}
                >
                  {contact.subtitle}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#9A9A9A', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', paddingBottom: '5px', cursor: 'pointer' }}
                  onClick={() => window.open(contact.link, '_blank')}
                >
                  {contact.text}
                </Typography>
                {/* Careers email sits directly under the Email entry */}
                {contact.id === '2' && (
                  <Typography
                    variant="caption"
                    sx={{ color: '#9A9A9A', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', paddingBottom: '5px', cursor: 'pointer' }}
                    onClick={() => window.open('mailto:careers@hertex.co.za', '_blank')}
                  >
                    careers@hertex.co.za
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Mobile */}

        <Divider
          sx={{
            backgroundColor: '#6E6E6E',
            mt: 3,
          }}
        ></Divider>
          <Grid
              container
              direction="column"
              sx={{
                  mt: { xs: 3, md: 3 },
                  px: 2, // Optional: Add some horizontal padding
              }}
              alignItems="center"
          >
              {/* Combined Consumer Notice and Reserved Rights */}
              <Grid item sx={{ width: '100%' }}>
                  <Typography
                      variant="caption"
                      fontWeight="600"
                      color="#FFF"
                      align="center"
                  >
                      All content, including images, text, and designs on this website, is protected by copyright. Unauthorized use, reproduction, or distribution without written permission is prohibited. © {dateNow.getFullYear()} Hertex Fabrics (Pty) Ltd. All rights reserved.
                  </Typography>
              </Grid>

              {/* Social Media Icons on Separate Line */}
            <Grid item sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                }}
              >
                <Link
                  href="https://www.facebook.com/hertexfabrics/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  sx={{ color: '#FFF' }}
                >
                  <FacebookIcon sx={{ fontSize: 24 }} />
                </Link>
                <Link
                  href="https://www.instagram.com/hertexfabrics/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  sx={{ color: '#FFF' }}
                >
                  <InstagramIcon sx={{ fontSize: 24 }} />
                </Link>
                <Link
                  href="https://www.linkedin.com/company/hertexfabrics/mycompany/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  sx={{ color: '#FFF' }}
                >
                  <LinkedInIcon sx={{ fontSize: 24 }} />
                </Link>
                <Link
                  href="https://za.pinterest.com/hertexfabrics/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Pinterest"
                  sx={{ color: '#FFF' }}
                >
                  <PinterestIcon sx={{ fontSize: 24 }} />
                </Link>
                <Link
                  href="https://www.tiktok.com/@hertexfabrics?_r=1&_t=ZS-94wuwJXOEh2"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  sx={{ color: '#FFF' }}
                >
                  <SvgIcon sx={{ fontSize: 24 }} viewBox="0 0 448 512">
                    <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"/>
                  </SvgIcon>
                </Link>
              </Box>
            </Grid>
          </Grid>


      </Container>
    </Grid>
  );
}

export default Footer;
